// @givmo/fundraisers-module — a campaign's Social tab (EMC-WEB5; Email Campaign plan §1.1 step 7, §2.7),
// to walkthrough frames 39–40 Jonathan approved: the campaign's posts on their dates with where each account's
// post stands, and Social's composer rented in place (D-EMC-1: the appeal feels like one product, so writing
// a post never leaves the campaign).
//
// ⚠ EVERY FACT IS SOCIAL'S. Whether the tab shows is the read's `available` (Social offered to the
// organization and switched on); a post is its time, an excerpt, its image where Social serves one and
// each account's state, never an author, a failure's detail or an engagement figure. A post's reason and
// its Retry are Social's own ("See why" opens the post in Social's card). The composer is Social's,
// embedded through the host (`config.social.composer`) as frame 40's card, always there for a person who
// writes posts; the tab hands it the campaign, the campaign's short link and a time beside the email
// series, and writes no words of the post itself. Once the campaign has posts they stand beside the card
// in place of the editor's side cards (frame 40); before then the editor keeps them (frame 39).

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { orgFundraisersGetOrgFundraiserShareOptions } from '@givmo/api-client/query';
import { Share2 } from 'lucide-react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  cn,
  EmptyState,
  Icon,
  testIdProp,
} from '@givmo/design-system';
import { CAMPAIGN_SOCIAL_COPY as C } from './campaign-social-copy';
import {
  groupPosts,
  isCampaignSocialUnavailable,
  type CampaignSocial,
  type SocialAccountRow,
  type SocialAccountState,
  type SocialPostRow,
} from './campaign-social-decode';
import { campaignSocialKeys, campaignSocialOptions } from './campaign-social-queries';
import { emailDay, emailTime } from './emails-decode';
import { emailsSeriesOptions } from './emails-queries';
import type { FundraisersSocial } from './fundraisers-config';
import { SUPPORT_EMAIL } from './fundraisers-decode';
import { useFundraisersModule } from './fundraisers-context';
import { FundraisersLoading } from './fundraisers-state';

/** The states a person may need to act on: each opens the post in Social, where its reason is. */
const NEEDS_A_LOOK: ReadonlySet<SocialAccountState> = new Set(['attention', 'unknown', 'failed']);
/** A post nothing has gone out from: its composer may still change it. */
const CHANGEABLE: ReadonlySet<SocialAccountState> = new Set(['draft', 'scheduled', 'deferred']);

/** Social's posts here, while the read answers; a route miss is no Social (the tab is hidden). */
export function useCampaignSocial(orgRef: string | null, fundraiserId: string, enabled: boolean) {
  return useQuery({
    ...campaignSocialOptions(orgRef ?? '', fundraiserId),
    enabled: enabled && orgRef !== null,
  });
}

/**
 * The tab's own address while Social isn't served here (the Emails tab's pattern), in the org console's
 * calm notice, saying who turns Social on: Givmo, at its support address.
 */
export function CampaignSocialUnavailable() {
  const { orgName } = useFundraisersModule();
  return (
    <Card>
      <CardBody className="flex flex-col gap-8">
        <h3 className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
          {C.unavailableTitle}
        </h3>
        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
          {...testIdProp('fundraiser-social-unavailable')}
        >
          {C.unavailableBody(orgName)}{' '}
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Turn on Social for ${orgName}`)}`}
            className="font-w600 text-brand-700 hover:underline"
            {...testIdProp('fundraiser-social-unavailable-contact')}
          >
            {SUPPORT_EMAIL}
          </a>
        </p>
      </CardBody>
    </Card>
  );
}

/**
 * The next email of the series still to go out: the time a new post is suggested for ("beside the email
 * series"). A prefill the person confirms in Social's composer, never a send.
 */
function nextEmailTime(
  rows: readonly { scheduledFor: string | null; state: string }[],
  now: Date,
): string | undefined {
  const upcoming = rows
    .filter((row) => row.state === 'scheduled' && row.scheduledFor !== null)
    .map((row) => row.scheduledFor as string)
    .filter((at) => new Date(at).getTime() > now.getTime())
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  return upcoming[0];
}

/** "ET": the zone's everyday short name, as frame 40's time reads ("9:00 AM ET"); null without one. */
function zoneShort(zone: string | null, at: string): string | null {
  if (zone === null) return null;
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortGeneric' })
        .formatToParts(new Date(at))
        .find((part) => part.type === 'timeZoneName')?.value ?? null
    );
  } catch {
    return null;
  }
}

/** A post's time in the organization's zone, named: Social's card beside it names the zone it shows. */
function WhenCell({ at, zone }: { at: string | null; zone: string | null }) {
  if (at === null) return <span className="text-text-secondary">{C.noTime}</span>;
  const short = zoneShort(zone, at);
  return (
    <span className="whitespace-nowrap">
      <span className="font-w600 text-text-strong">{emailDay(at, zone)}</span>
      <span className="text-text-secondary">
        {' '}
        · {emailTime(at, zone)}
        {short === null ? '' : ` ${short}`}
      </span>
    </span>
  );
}

/** One post's accounts by where they stand, in the order each state first appears: most posts read as
 *  one line ("Scheduled · Facebook, Instagram, LinkedIn"). */
function byState(accounts: readonly SocialAccountRow[]) {
  const groups: { state: SocialAccountState; accounts: SocialAccountRow[] }[] = [];
  for (const account of accounts) {
    const group = groups.find((entry) => entry.state === account.state);
    if (group === undefined) groups.push({ state: account.state, accounts: [account] });
    else group.accounts.push(account);
  }
  return groups;
}

/** An account by its platform; by its own name too where the post goes to two of one platform. */
function accountLabel(account: SocialAccountRow, accounts: readonly SocialAccountRow[]): string {
  const platform = C.platform(account.platform);
  const repeated = accounts.filter((other) => other.platform === account.platform).length > 1;
  return repeated && account.accountName !== null
    ? `${platform} (${account.accountName})`
    : platform;
}

function StateLine({
  state,
  accounts,
  post,
  social,
  onOpen,
  testId,
}: {
  state: SocialAccountState;
  accounts: readonly SocialAccountRow[];
  post: SocialPostRow;
  social: FundraisersSocial;
  onOpen: (() => void) | null;
  testId: string;
}) {
  const config = useFundraisersModule();
  const look = NEEDS_A_LOOK.has(state);
  const names = accounts.map((account) => accountLabel(account, post.accounts));
  // Inline words, spaced as text, so the line reads aloud as it reads on screen (Social's lines:
  // "Didn't post · Instagram — See why"; "Couldn't confirm it posted · Check Instagram before trying
  // again", where it may have posted, with the same way to its Retry).
  return (
    <li className="text-s14 leading-[20px] wrap-anywhere" {...testIdProp(testId)}>
      <span
        className={look ? 'font-w600 text-warning-700' : 'text-text-secondary'}
        {...testIdProp(`${testId}-state`)}
      >
        {C.state[state]}
      </span>{' '}
      <span aria-hidden className="text-text-tertiary">
        ·
      </span>{' '}
      {state === 'unknown' ? (
        <span className="text-text-strong">{C.checkFirst(names)}</span>
      ) : (
        <span className="text-text-strong">
          {accounts.map((account, index) => (
            <span key={`${account.platform}-${index}`}>
              {index > 0 ? ', ' : ''}
              {account.permalink !== null ? (
                // A published post's platform name opens it there.
                <a
                  href={account.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-w600 text-brand-700 hover:underline"
                  {...testIdProp(`${testId}-view-${index}`)}
                >
                  {names[index]}
                </a>
              ) : (
                names[index]
              )}
            </span>
          ))}
        </span>
      )}
      {look && social.canRead && (
        <>
          {' '}
          <span aria-hidden className="text-text-tertiary">
            —
          </span>{' '}
          <a
            href={social.postHref(post.postId)}
            onClick={(event) => {
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
              event.preventDefault();
              if (onOpen !== null) onOpen();
              else config.onNavigate(social.postHref(post.postId));
            }}
            className="font-w600 text-brand-700 hover:underline"
            {...testIdProp(`${testId}-why`)}
          >
            {C.seeWhy}
          </a>
        </>
      )}
    </li>
  );
}

function PostRow({
  post,
  social,
  zone,
  beside,
  selected,
  onOpen,
  onEdit,
}: {
  post: SocialPostRow;
  social: FundraisersSocial;
  zone: string | null;
  /** In frame 40's narrower column beside the composer. */
  beside: boolean;
  /** Open in the composer beside the list. */
  selected: boolean;
  /** Opens the post in place (Social's own view): "See why" goes there, not out to Social. */
  onOpen: (() => void) | null;
  onEdit: (() => void) | null;
}) {
  const testId = `fundraiser-social-post-${post.postId}`;
  return (
    <li
      className={cn(
        'flex gap-12 border-b border-border-subtle px-20 py-16 last:border-b-0',
        selected && 'bg-brand-50',
      )}
      aria-current={selected ? 'true' : undefined}
      {...testIdProp(testId)}
    >
      {/* Frame 40's thumbnail: the post's image where Social serves it (SMM-BE22), nothing without. */}
      {post.imageUrl !== null && (
        <img
          src={post.imageUrl}
          alt=""
          loading="lazy"
          className="size-36 shrink-0 rounded-md border border-border-subtle object-cover"
          {...testIdProp(`${testId}-image`)}
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-6">
        <div className="flex items-start justify-between gap-12">
          <p className="text-s14">
            <WhenCell at={post.at} zone={zone} />
          </p>
          {onEdit !== null && (
            <Button
              appearance="saas"
              tone="link"
              size="sm"
              onClick={onEdit}
              testId={`${testId}-edit`}
            >
              {C.editPost}
            </Button>
          )}
        </div>
        <p
          className={cn(
            'text-text-strong wrap-anywhere',
            // Beside the card, frame 40's one line a post: its card opens it whole.
            beside ? 'truncate text-s14 leading-[20px]' : 'line-clamp-3 text-s15 leading-[22px]',
          )}
        >
          {post.excerpt.trim().length > 0 ? post.excerpt : C.noWords}
        </p>
        {post.accounts.length > 0 && (
          <ul className="flex flex-col gap-4">
            {byState(post.accounts).map((group) => (
              <StateLine
                key={group.state}
                state={group.state}
                accounts={group.accounts}
                post={post}
                social={social}
                onOpen={onOpen}
                testId={`${testId}-${group.state}`}
              />
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

/**
 * D-EMC-10: a person who reads the campaign but can't write posts is told who decides, with the host's
 * door (the shell's ONE request dialog, which goes to Social's owners and the organization's Global
 * Admins, who are notified); once asked, it says so in words, not a greyed button.
 */
function AccessDoor({ social }: { social: FundraisersSocial }) {
  const { orgName } = useFundraisersModule();
  if (social.canPublish || social.onRequestAccess === undefined) return null;
  const pending = social.accessRequestPending === true;
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-12 border-t border-border-subtle px-20 py-14"
      {...testIdProp('fundraiser-social-access')}
    >
      <p className="text-s14 text-text-secondary">
        {pending ? C.accessRequested(orgName) : C.accessLine(orgName)}
      </p>
      {!pending && (
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          aria-label={C.requestAccessLabel}
          onClick={social.onRequestAccess}
          testId="fundraiser-social-request-access"
        >
          {C.requestAccess}
        </Button>
      )}
    </div>
  );
}

/** Frame 40's columns: Social's composer, and the campaign's posts beside it at frame 40's width. */
const BESIDE_COLUMNS = 'grid grid-cols-1 items-start gap-24 xl:grid-cols-[minmax(0,1fr)_400px]';

/**
 * Frame 40: where this person writes posts here and the campaign has some, its posts stand beside Social's
 * composer, in place of the editor's side cards (the editor asks this too). Before the first post the
 * editor keeps its cards beside the composer (frame 39).
 */
export function postsBesideComposer(
  social: FundraisersSocial | null | undefined,
  read: CampaignSocial | undefined,
): boolean {
  return (
    social != null &&
    social.composer !== undefined &&
    social.canPublish &&
    read !== undefined &&
    read.available &&
    read.posts.length > 0
  );
}

export function CampaignSocialTab({
  fundraiserId,
  live,
}: {
  fundraiserId: string;
  /** The campaign is public: its short link exists for a post to carry. */
  live: boolean;
}) {
  const config = useFundraisersModule();
  const social = config.social ?? null;
  const orgRef = config.orgRef;
  const client = useQueryClient();
  const posts = useCampaignSocial(orgRef, fundraiserId, social !== null);
  const series = useQuery({
    ...emailsSeriesOptions(orgRef ?? '', fundraiserId),
    enabled: orgRef !== null && social?.composer !== undefined,
  });
  const share = useQuery({
    ...orgFundraisersGetOrgFundraiserShareOptions({
      path: { org_ref: orgRef ?? '', fundraiser_id: fundraiserId },
    }),
    enabled: orgRef !== null && live && social?.composer !== undefined,
    retry: false,
  });
  /** The post open in Social's card; null: a new post (for a person who writes them). */
  const [opened, setOpened] = useState<string | null>(null);
  const now = new Date();
  const zone = config.orgTimeZone ?? null;

  if (social === null || orgRef === null) return <CampaignSocialUnavailable />;
  if (posts.isError && isCampaignSocialUnavailable(posts.error)) {
    return <CampaignSocialUnavailable />;
  }
  if (posts.isSuccess && !posts.data.available) return <CampaignSocialUnavailable />;

  const Composer = social.composer;
  const canCompose = Composer !== undefined && social.canPublish;
  const beside = postsBesideComposer(social, posts.data);
  // What Social's card changed, the list reads again (Social's own reads refresh themselves).
  const reread = () =>
    void client.invalidateQueries({ queryKey: campaignSocialKeys.posts(orgRef, fundraiserId) });
  const close = () => {
    setOpened(null);
    reread();
  };
  /** A post opened in place: Social's composer for one still changeable, its own view for the rest. */
  const openPost = Composer !== undefined && social.canRead ? setOpened : null;
  const { drafts, dated } = posts.isSuccess
    ? groupPosts(posts.data.posts)
    : { drafts: [], dated: [] };
  const row = (post: SocialPostRow) => (
    <PostRow
      key={post.postId}
      post={post}
      social={social}
      zone={zone}
      beside={beside}
      selected={opened === post.postId}
      onOpen={openPost === null ? null : () => openPost(post.postId)}
      onEdit={
        canCompose &&
        opened !== post.postId &&
        post.accounts.length > 0 &&
        post.accounts.every((account) => CHANGEABLE.has(account.state))
          ? () => setOpened(post.postId)
          : null
      }
    />
  );

  // Social's card reads its prefills once, as it opens: it waits for the campaign's link and the email
  // series (a read that fails, or a link a draft doesn't have yet, opens it without that prefill).
  const prefillsRead = !share.isLoading && !series.isLoading;
  // Social's card as Social draws it (frame 40): always a new post for a person who writes them, or the
  // post opened from the list; for a person who reads them, only a post opened from the list.
  const composer =
    Composer !== undefined &&
    (canCompose || opened !== null) &&
    (prefillsRead ? (
      <Composer
        orgRef={orgRef}
        orgName={config.orgName}
        timeZone={zone}
        permissions={config.permissions}
        context={{ kind: 'fundraiser_campaign', id: fundraiserId }}
        prefill={{
          link: share.data?.share_url ?? undefined,
          // Beside the email series; a reopened post's own time comes first (Social's rule).
          suggestedAt: series.isSuccess ? nextEmailTime(series.data, now) : undefined,
        }}
        postId={opened ?? undefined}
        onDone={opened === null ? reread : close}
        onCancel={opened === null ? undefined : close}
        onDeleted={close}
      />
    ) : (
      <Card flush testId="fundraiser-social-composer-loading">
        <CardBody>
          <FundraisersLoading />
        </CardBody>
      </Card>
    ));

  const list = (
    <Card flush testId="fundraiser-social-posts">
      <CardHeader title={C.title} />
      {posts.isError ? (
        <CardBody>
          <EmptyState
            testId="fundraiser-social-error"
            title={C.loadFailed}
            action={
              <Button
                appearance="saas"
                tone="secondary"
                testId="fundraiser-social-retry"
                onClick={() => void posts.refetch()}
              >
                {C.retry}
              </Button>
            }
          />
        </CardBody>
      ) : posts.isPending ? (
        <CardBody>
          <FundraisersLoading />
        </CardBody>
      ) : drafts.length + dated.length === 0 ? (
        <CardBody>
          <EmptyState
            testId="fundraiser-social-empty"
            icon={<Icon icon={Share2} size={20} aria-hidden />}
            title={C.empty}
          />
        </CardBody>
      ) : (
        // The drafts grouped first under their own small label, then every other post by when it goes
        // out or went out, the earliest first (the user test, 2026-11-03).
        <div {...testIdProp('fundraiser-social-list')}>
          {drafts.length > 0 && (
            <section aria-labelledby="fundraiser-social-drafts-label">
              <p
                id="fundraiser-social-drafts-label"
                className="px-20 pt-14 font-sans text-s12 font-w600 uppercase tracking-[0.04em] text-text-tertiary"
              >
                {C.drafts}
              </p>
              <ul {...testIdProp('fundraiser-social-drafts')}>{drafts.map(row)}</ul>
            </section>
          )}
          {dated.length > 0 && (
            <ul
              className={cn(drafts.length > 0 && 'border-t border-border-subtle')}
              {...testIdProp('fundraiser-social-dated')}
            >
              {dated.map(row)}
            </ul>
          )}
        </div>
      )}
      <AccessDoor social={social} />
    </Card>
  );

  // One tree in both layouts, each part keyed, so Social's card never remounts (and never loses what the
  // person is writing) when the campaign's first post moves the list beside it.
  return (
    <div
      className={beside ? BESIDE_COLUMNS : 'flex min-w-0 flex-col gap-20'}
      {...testIdProp('fundraiser-social')}
    >
      {composer && (
        <div key="composer" className="min-w-0">
          {composer}
        </div>
      )}
      {/* Beside a writer's card, the list waits for the campaign's first post: until then the editor's
          own cards stand beside it (frame 39). A read that failed still offers Try again. */}
      {(beside || !canCompose || posts.isError) && (
        <div
          key="list"
          className="min-w-0"
          {...testIdProp(beside ? 'fundraiser-social-beside' : undefined)}
        >
          {list}
        </div>
      )}
    </div>
  );
}
